import {FC, ReactNode, useState} from 'react'
import {useForm, SubmitHandler} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import {yup} from 'libs/yup'
import Button from 'jbc-front/components/Button'
import {Modal} from 'jbc-front/components/presenters/ui/Modal'
import {ButtonRow} from 'jbc-front/components/presenters/layout/ButtonRow'
import {Label} from 'jbc-front/components/presenters/form/Label'
import {Textarea} from 'jbc-front/components/presenters/form/Textarea'
import {ErrorMessage} from 'jbc-front/components/presenters/form/ErrorMessage'
import {Spacer} from 'jbc-front/components/presenters/Spacer'
import {SubmittingData} from 'components/SubmittingData'
import styles from './CommentModal.scss'

// @ts-ignore
import ReviewComments from 'components/ReviewComments'

const schema = yup.object({
  comment: yup.string().notRequired().ensure().max(10000).label('コメント'),
})

type Schema = yup.InferType<typeof schema>

type Comment = unknown

interface FormData {
  comment: string
}

interface Props {
  title: string
  placeholder?: string
  isOpen: boolean
  onSubmit: (data: FormData) => Promise<void>
  onCancel?: () => Promise<void>
  onClose: () => void
  comments?: Comment[]
}

export const CommentModal: FC<Props> = ({
  title,
  placeholder,
  isOpen,
  onSubmit: onSubmitProp,
  onCancel: onCancelProp,
  onClose,
  comments = [],
}) => {
  const {
    register,
    handleSubmit,
    formState: {errors},
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      comment: '',
    },
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const onSubmit: SubmitHandler<Schema> = async ({comment}) => {
    setIsSubmitting(true)
    try {
      await onSubmitProp({comment})
      onClose()
    } finally {
      setIsSubmitting(false)
    }
  }

  const onCancel = async () => {
    setIsSubmitting(true)
    try {
      await onCancelProp?.()
      onClose()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <Modal.Header onClose={onCancel}>{title}</Modal.Header>
        <Modal.Body>
          <div className={styles.commentWrap}>
            <ReviewComments comments={comments} widthAuto />
            <div>
              <Label>コメント</Label>
              <Spacer size={8} />
              <Textarea
                {...register('comment')}
                className={styles.textarea}
                placeholder={placeholder}
                isError={!!errors.comment?.message}
              />
              {errors.comment?.message && <ErrorMessage>{errors.comment.message}</ErrorMessage>}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <ButtonRow>
            <Button onClick={onCancel} disabled={isSubmitting}>
              キャンセル
            </Button>
            <Button primary onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
              送信
            </Button>
          </ButtonRow>
        </Modal.Footer>
      </Modal>
      <SubmittingData isSubmitting={isSubmitting} />
    </>
  )
}

interface CommentModalWithChildrenProps extends Omit<Props, 'isOpen' | 'onClose'> {
  children: (showModal: () => void, isOpen: boolean) => ReactNode
}

/** @deprecated Please use {@link CommentModal} and manage state in caller. */
export const CommentModalWithChildren: FC<CommentModalWithChildrenProps> = ({children, ...props}) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <CommentModal isOpen={isOpen} onClose={() => setIsOpen(false)} {...props} />
      {children(() => setIsOpen(true), isOpen)}
    </>
  )
}
